// src/pages/finance/PaymentForm.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  Grid,
  CircularProgress
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { es } from 'date-fns/locale';

const PaymentForm = ({ open, onClose, onSubmit, invoice, paymentMethods, currencySymbol = '$' }) => {
  const [formData, setFormData] = useState({
    amount: '',
    payment_date: new Date(),
    payment_method: '',
    payment_reference: '',
    proof_image_url: '',
    activate_service: true,
    document_type: 'recibo',
    client_document_type: 'DNI',
    client_document_number: '',
    client_business_name: '',
    covered_months: 2
  });

  const getAdvanceCoverageDetails = (monthsCount) => {
    const num = parseInt(monthsCount || 0, 10);
    if (!num || num <= 0) return null;
    
    // Si el cliente ya tiene un adelanto vigente, sumamos a partir de esa fecha
    let baseDate = new Date();
    if (invoice.advance_paid_until && new Date(invoice.advance_paid_until) > baseDate) {
      baseDate = new Date(invoice.advance_paid_until);
    }
    
    const monthNames = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    
    const firstCoveredMonthIndex = (baseDate.getMonth() + 1) % 12;
    const firstCoveredYear = baseDate.getFullYear() + Math.floor((baseDate.getMonth() + 1) / 12);

    const lastCoveredMonthIndex = (baseDate.getMonth() + num) % 12;
    const lastCoveredYear = baseDate.getFullYear() + Math.floor((baseDate.getMonth() + num) / 12);
    
    const nextInvoiceMonthIndex = (baseDate.getMonth() + num + 1) % 12;
    const nextInvoiceYear = baseDate.getFullYear() + Math.floor((baseDate.getMonth() + num + 1) / 12);

    const endDate = new Date(lastCoveredYear, lastCoveredMonthIndex + 1, 0);

    let monthsStr = '';
    if (num === 1) {
      monthsStr = monthNames[firstCoveredMonthIndex] + ' ' + firstCoveredYear;
    } else {
      monthsStr = `${monthNames[firstCoveredMonthIndex]} a ${monthNames[lastCoveredMonthIndex]} ${lastCoveredYear}`;
    }

    return {
      monthsRange: monthsStr,
      endDateFormatted: `${endDate.getDate()}/${endDate.getMonth() + 1}/${endDate.getFullYear()}`,
      nextInvoiceMonth: `${monthNames[nextInvoiceMonthIndex]} ${nextInvoiceYear}`
    };
  };

  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [ocrAnalyzing, setOcrAnalyzing] = useState(false);
  const [ocrStatus, setOcrStatus] = useState(null);
  const [ocrMessage, setOcrMessage] = useState('');

  // Función utilitaria para comprimir imágenes usando HTML5 Canvas (idéntica al portal del cliente)
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Ajustar dimensiones proporcionalmente a un máximo de 1000px
          const MAX_WIDTH = 1000;
          const MAX_HEIGHT = 1000;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height = Math.round((height * MAX_WIDTH) / width);
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width = Math.round((width * MAX_HEIGHT) / height);
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Exportar como blob JPEG con calidad del 75%
          canvas.toBlob((blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              resolve(compressedFile);
            } else {
              reject(new Error('Error al comprimir la imagen'));
            }
          }, 'image/jpeg', 0.75);
        };
        img.onerror = (err) => reject(new Error('Error al cargar la imagen en memoria'));
      };
      reader.onerror = (err) => reject(new Error('Error al leer el archivo'));
    });
  };

  const processOcrAndUpload = async (file) => {
    if (!file) return;

    setOcrAnalyzing(true);
    setOcrStatus(null);
    setOcrMessage('');

    try {
      const token = localStorage.getItem('token');
      
      // Comprimir la imagen antes de subirla
      let fileToUpload = file;
      try {
        fileToUpload = await compressImage(file);
        console.log('✅ Imagen comprimida para OCR admin. Tamaño original:', file.size, 'Comprimido:', fileToUpload.size);
      } catch (compressError) {
        console.error('Error al comprimir imagen, subiendo original:', compressError);
      }

      // Determinar si el método seleccionado actualmente es efectivo
      const selectedMethodObj = paymentMethods.find(m => m.id === formData.payment_method);
      const isCash = selectedMethodObj ? selectedMethodObj.name.toLowerCase().includes('efectivo') : false;

      const uploadFormData = new FormData();
      uploadFormData.append('proof', fileToUpload);
      
      const clientId = invoice ? (invoice.client_id || invoice.id) : '';
      const queryParams = new URLSearchParams();
      if (isCash) queryParams.append('isCash', 'true');
      if (clientId) queryParams.append('clientId', clientId);

      const response = await fetch(`/api/payments/upload?${queryParams.toString()}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: uploadFormData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Error al subir el comprobante.');
      }

      const data = await response.json();
      console.log('[OCR Admin Result]', data);

      setFormData(prev => ({
        ...prev,
        proof_image_url: data.proof_image_url
      }));

      const ocr = data.ocr || {};
      if (ocr.success && !isCash) {
        setOcrStatus('success');
        setOcrMessage('¡Comprobante escaneado con éxito! Se han autocompletado los campos.');
        
        // Auto-llenar campos
        setFormData(prev => {
          const updated = { ...prev };
          if (ocr.amount) updated.amount = ocr.amount;
          if (ocr.reference) updated.payment_reference = ocr.reference;
          
          if (ocr.method && ocr.method !== 'unknown') {
            const cleanOcrMethod = ocr.method.toLowerCase();
            const foundMethod = paymentMethods.find(m => {
              const cleanName = m.name.toLowerCase();
              return cleanName.includes(cleanOcrMethod) || 
                     (cleanOcrMethod === 'yape' && cleanName.includes('yape')) ||
                     (cleanOcrMethod === 'plin' && cleanName.includes('plin')) ||
                     (cleanOcrMethod === 'bcp' && (cleanName.includes('bcp') || cleanName.includes('crédito'))) ||
                     (cleanOcrMethod === 'bbva' && (cleanName.includes('bbva') || cleanName.includes('continental'))) ||
                     (cleanOcrMethod === 'interbank' && cleanName.includes('interbank'));
            });
            if (foundMethod) {
              updated.payment_method = foundMethod.id;
            }
          }
          return updated;
        });
      } else {
        setOcrStatus(isCash ? null : 'failed');
        if (!isCash) {
          setOcrMessage('No pudimos leer los datos automáticamente. Por favor completa los campos manualmente.');
        }
      }
    } catch (err) {
      console.error('[OCR Admin Error]', err);
      setOcrStatus('failed');
      setOcrMessage(err.message || 'Error al procesar el comprobante.');
    } finally {
      setOcrAnalyzing(false);
    }
  };

  useEffect(() => {
    if (invoice) {
      console.log('Datos de factura recibidos:', invoice);
      
      const total = typeof invoice.amount === 'number' ? invoice.amount : 
                   (invoice.amount ? parseFloat(invoice.amount) : 0);
      
      const paid = typeof invoice.paid_amount === 'number' ? invoice.paid_amount : 
                   (invoice.paid_amount ? parseFloat(invoice.paid_amount) : 0);
      
      const pending = total - paid;
      
      if (invoice.isAdvancePayment) {
        const defaultMonths = invoice.covered_months || 2;
        const planPrice = parseFloat(invoice.plan_price || 0);
        const calculatedAmount = planPrice > 0 ? planPrice * defaultMonths : (pending > 0 ? pending : '');

        setFormData(prev => ({
          ...prev,
          amount: calculatedAmount,
          covered_months: defaultMonths,
          document_type: invoice.document_type || 'boleta',
          client_document_type: invoice.client_document_type || (invoice.client_business_name ? 'RUC' : 'DNI'),
          client_document_number: invoice.client_document_number || invoice.client_dni || '',
          client_business_name: invoice.client_business_name || ''
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          amount: pending > 0 ? pending : 0,
          document_type: invoice.document_type || 'recibo',
          client_document_type: invoice.client_document_type || 'DNI',
          client_document_number: invoice.client_document_number || '',
          client_business_name: invoice.client_business_name || ''
        }));
      }
    }
  }, [invoice]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      
      if (name === 'covered_months' && invoice?.isAdvancePayment) {
        const months = parseInt(value || 0, 10);
        const planPrice = parseFloat(invoice?.plan_price || 0);
        if (months > 0 && planPrice > 0) {
          updated.amount = (months * planPrice).toFixed(2);
        }
      }

      if (name === 'document_type') {
        if (value === 'factura') {
          updated.client_document_type = 'RUC';
        } else {
          updated.client_document_type = 'DNI';
        }
      }
      
      return updated;
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      processOcrAndUpload(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.payment_method) {
      setError('Debe seleccionar un método de pago');
      return;
    }

    if (parseFloat(formData.amount) <= 0) {
      setError('El monto debe ser mayor a 0');
      return;
    }

    // Validaciones fiscales de comprobantes
    if (formData.document_type === 'boleta') {
      const docNum = formData.client_document_number.replace(/\D/g, '');
      if (docNum.length !== 8) {
        setError('El D.N.I. de la boleta debe contener exactamente 8 números.');
        return;
      }
    } else if (formData.document_type === 'factura') {
      const docNum = formData.client_document_number.replace(/\D/g, '');
      if (docNum.length !== 11) {
        setError('El R.U.C. de la factura debe contener exactamente 11 números.');
        return;
      }
      if (!formData.client_business_name.trim()) {
        setError('Debe ingresar la Razón Social para emitir una Factura.');
        return;
      }
    }

    onSubmit({
      ...formData
    });
  };

  if (!invoice) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)'
        }
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontWeight: 800, pb: 1, color: '#0f172a' }}>
          {invoice.isAdvancePayment ? 'Registrar Pago Adelantado (Abono al Saldo)' : 'Registrar Pago'}
        </DialogTitle>
        <DialogContent>
          <Box className="space-y-4 py-4">
            {error && (
              <Alert severity="error" sx={{ borderRadius: '14px', mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box 
              sx={{ 
                p: 3, 
                borderRadius: '16px', 
                backgroundColor: 'rgba(99, 102, 241, 0.05)', 
                border: '1px solid rgba(99, 102, 241, 0.1)',
                mb: 3
              }}
            >
              {invoice.isAdvancePayment ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>Tipo de Operación</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#6366f1' }}>Abono Directo a Saldo a Favor</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>Cliente</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#1e293b' }}>{invoice.client_name}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>Saldo a Favor Actual</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 800, color: '#10b981', fontSize: '1.1rem' }}>
                      {currencySymbol} {parseFloat(invoice.balance || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>Factura Original</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#1e293b' }}>N° {invoice.invoice_number}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>Cliente</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#1e293b' }}>{invoice.client_name}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>Monto Total</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#334155' }}>
                      {currencySymbol} {typeof invoice.amount === 'number' 
                        ? invoice.amount.toFixed(2) 
                        : parseFloat(invoice.amount || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>Monto Cobrado</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#334155' }}>
                      {currencySymbol} {typeof invoice.paid_amount === 'number' 
                        ? invoice.paid_amount.toFixed(2) 
                        : parseFloat(invoice.paid_amount || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>Saldo Pendiente</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: '#ef4444' }}>
                      {currencySymbol} {(parseFloat(invoice.amount || 0) - parseFloat(invoice.paid_amount || 0)).toFixed(2)}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Box>

            <Grid container spacing={3}>
              {invoice.isAdvancePayment && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Meses a Adelantar"
                    name="covered_months"
                    value={formData.covered_months}
                    onChange={handleChange}
                    required
                    helperText={invoice.plan_price ? `Plan del cliente: ${currencySymbol} ${parseFloat(invoice.plan_price).toFixed(2)}/mes` : ''}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  >
                    <MenuItem value={1}>1 Mes de Servicio</MenuItem>
                    <MenuItem value={2}>2 Meses (Recomendado)</MenuItem>
                    <MenuItem value={3}>3 Meses</MenuItem>
                    <MenuItem value={4}>4 Meses</MenuItem>
                    <MenuItem value={6}>6 Meses (Medio Año)</MenuItem>
                    <MenuItem value={12}>12 Meses (1 Año Completo)</MenuItem>
                    <MenuItem value={0}>0 / Solo Abono a Saldo Libre</MenuItem>
                  </TextField>
                </Grid>
              )}

              <Grid item xs={12} md={invoice.isAdvancePayment ? 6 : 6}>
                <TextField
                  fullWidth
                  label={invoice.isAdvancePayment ? "Monto Total del Adelanto" : "Monto a Pagar"}
                  name="amount"
                  type="number"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: `${currencySymbol} `
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Método de Pago"
                  name="payment_method"
                  value={formData.payment_method}
                  onChange={handleChange}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                >
                  {paymentMethods.map(method => (
                    <MenuItem key={method.id} value={method.id}>
                      {method.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
                  <DateTimePicker
                    label="Fecha y Hora de Pago"
                    value={formData.payment_date}
                    onChange={(date) => handleChange({
                      target: { name: 'payment_date', value: date }
                    })}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        sx: { '& .MuiOutlinedInput-root': { borderRadius: '12px' } }
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Referencia de Pago"
                  name="payment_reference"
                  value={formData.payment_reference}
                  onChange={handleChange}
                  placeholder="Nro. de Operación, Transferencia, etc."
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>

              {/* SECCIÓN COMPROBANTE OFICIAL DINÁMICO */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#334155', mt: 1, mb: -1 }}>
                  ⚙️ Comprobante Oficial a Emitir
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Tipo de Comprobante"
                  name="document_type"
                  value={formData.document_type}
                  onChange={handleChange}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                >
                  <MenuItem value="recibo">Recibo Interno (Servicio exento)</MenuItem>
                  <MenuItem value="boleta">Boleta de Venta (Retail/DNI)</MenuItem>
                  <MenuItem value="factura">Factura de Venta (B2B/RUC)</MenuItem>
                </TextField>
              </Grid>

              {formData.document_type === 'boleta' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="D.N.I. del Cliente"
                    name="client_document_number"
                    value={formData.client_document_number}
                    onChange={handleChange}
                    required
                    inputProps={{ maxLength: 8 }}
                    helperText="Requerido: exactamente 8 dígitos"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                  />
                </Grid>
              )}

              {formData.document_type === 'factura' && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="R.U.C. del Cliente"
                      name="client_document_number"
                      value={formData.client_document_number}
                      onChange={handleChange}
                      required
                      inputProps={{ maxLength: 11 }}
                      helperText="Requerido: exactamente 11 dígitos"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Razón Social (Nombre de la Empresa)"
                      name="client_business_name"
                      value={formData.client_business_name}
                      onChange={handleChange}
                      required
                      placeholder="Ejemplo: Empresa S.A.C."
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                    />
                  </Grid>
                </>
              )}

              {invoice.isAdvancePayment && formData.covered_months > 0 && (() => {
                const details = getAdvanceCoverageDetails(formData.covered_months);
                if (!details) return null;
                const docLabel = formData.document_type === 'factura' ? 'Factura Electrónica' : (formData.document_type === 'boleta' ? 'Boleta de Venta Electrónica' : 'Recibo Interno');
                return (
                  <Grid item xs={12}>
                    <Alert 
                      severity="info" 
                      sx={{ 
                        borderRadius: '16px', 
                        backgroundColor: 'rgba(14, 165, 233, 0.08)',
                        border: '1px solid rgba(14, 165, 233, 0.2)',
                        '& .MuiAlert-icon': { color: '#0284c7', fontSize: '1.8rem' }
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0369a1', mb: 0.5 }}>
                        ⚡ Resumen de Cobertura y Operación SUNAT
                      </Typography>
                      <Typography variant="body2" component="div" sx={{ color: '#0c4a6e', fontSize: '0.825rem', lineHeight: 1.6 }}>
                        • <strong>Comprobante a Emitir:</strong> 1 {docLabel} por el total de <strong>{currencySymbol} {parseFloat(formData.amount || 0).toFixed(2)}</strong>.<br />
                        • <strong>Meses Cubiertos:</strong> {formData.covered_months} mes(es) ({details.monthsRange}).<br />
                        • <strong>Estado del Servicio:</strong> Activo sin cortes ni nuevos recibos hasta el <strong>{details.endDateFormatted}</strong>.<br />
                        • <strong>Próximo Recibo:</strong> Se generará automáticamente en <strong>{details.nextInvoiceMonth}</strong>.
                      </Typography>
                    </Alert>
                  </Grid>
                );
              })()}

              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  disabled={ocrAnalyzing}
                  sx={{ borderRadius: '12px', borderStyle: 'dashed', py: 1.5, textTransform: 'none' }}
                >
                  {ocrAnalyzing ? 'Subiendo y analizando...' : 'Subir Foto del Comprobante / Voucher'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    disabled={ocrAnalyzing}
                    onChange={handleFileChange}
                  />
                </Button>
                {selectedFile && (
                  <Typography variant="caption" display="block" sx={{ mt: 1, fontWeight: 600, color: '#6366f1' }}>
                    Archivo cargado: {selectedFile.name}
                  </Typography>
                )}
              </Grid>

              {ocrAnalyzing && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, bgcolor: 'rgba(99, 102, 241, 0.05)', borderRadius: '12px', border: '1px solid rgba(99, 102, 241, 0.15)' }}>
                    <CircularProgress size={20} color="primary" />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#4f46e5' }}>
                      Analizando comprobante con lector inteligente OCR...
                    </Typography>
                  </Box>
                </Grid>
              )}

              {ocrStatus === 'success' && ocrMessage && (
                <Grid item xs={12}>
                  <Alert severity="success" sx={{ borderRadius: '12px' }}>
                    {ocrMessage}
                  </Alert>
                </Grid>
              )}

              {ocrStatus === 'failed' && ocrMessage && (
                <Grid item xs={12}>
                  <Alert severity="warning" sx={{ borderRadius: '12px' }}>
                    {ocrMessage}
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.activate_service}
                      onChange={handleChange}
                      name="activate_service"
                      color="primary"
                    />
                  }
                  label="Re-activar automáticamente el servicio WISP al procesar cobro"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={onClose} sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600, color: '#64748b' }}>
            Cancelar
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={ocrAnalyzing}
            sx={{ 
              borderRadius: '12px', 
              textTransform: 'none', 
              px: 3, 
              fontWeight: 700, 
              backgroundColor: '#6366f1', 
              '&:hover': { backgroundColor: '#4f46e5' } 
            }}
          >
            {ocrAnalyzing ? 'Procesando OCR...' : 'Registrar Pago'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PaymentForm;